/**
 * C2C REST API
 *
 * Query fiat transaction history via the C2C REST API.
 *
 * The version of the OpenAPI document: 1.0.0
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
import {
    ConfigurationRestAPI,
    TimeUnit,
    RestApiResponse,
    sendRequest,
    type RequestArgs,
} from '@binance/common';
import type { GetC2CTradeHistoryResponse } from '../types';

/**
 * Api - axios parameter creator
 */
const ApiAxiosParamCreator = function (configuration: ConfigurationRestAPI) {
    return {
        /**
         * Get C2C Trade History
         *
         * Weight(IP): 1
         *
         * Security Type: USER_DATA
         *
         * Notes:
         * - The max interval between `startTimestamp` and `endTimestamp` is 30 days.
         * - If `startTimestamp` and `endTimestamp` are not sent, the recent 30 days' data is returned.
         * - You can only view data from the past 6 months. For all C2C orders, check `https://c2c.binance.com/en/fiatOrder`.
         *
         * @summary Get C2C Trade History (USER_DATA)
         * @param {GetC2CTradeHistoryTradeTypeEnum} [tradeType] Trade side filter
         * @param {number | bigint} [startTimestamp]
         * @param {number | bigint} [endTimestamp]
         * @param {number | bigint} [page] Page number
         * @param {number | bigint} [rows] Number of records per page
         *
         * @throws {RequiredError}
         */
        getC2CTradeHistory: async (
            tradeType?: GetC2CTradeHistoryTradeTypeEnum,
            startTimestamp?: number | bigint,
            endTimestamp?: number | bigint,
            page?: number | bigint,
            rows?: number | bigint
        ): Promise<RequestArgs> => {
            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (tradeType !== undefined && tradeType !== null) {
                localVarQueryParameter['tradeType'] = tradeType;
            }
            if (startTimestamp !== undefined && startTimestamp !== null) {
                localVarQueryParameter['startTimestamp'] = startTimestamp;
            }
            if (endTimestamp !== undefined && endTimestamp !== null) {
                localVarQueryParameter['endTimestamp'] = endTimestamp;
            }
            if (page !== undefined && page !== null) {
                localVarQueryParameter['page'] = page;
            }
            if (rows !== undefined && rows !== null) {
                localVarQueryParameter['rows'] = rows;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/sapi/v1/c2c/orderMatch/listUserOrderHistory',
                method: 'GET',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
    };
};

/**
 * Api - interface
 * @interface Api
 */
export interface ApiInterface {
    /**
     * Get C2C Trade History
     *
     * Weight(IP): 1
     *
     * Security Type: USER_DATA
     *
     * Notes:
     * - The max interval between `startTimestamp` and `endTimestamp` is 30 days.
     * - If `startTimestamp` and `endTimestamp` are not sent, the recent 30 days' data is returned.
     * - You can only view data from the past 6 months. For all C2C orders, check `https://c2c.binance.com/en/fiatOrder`.
     *
     * @summary Get C2C Trade History (USER_DATA)
     * @param {GetC2CTradeHistoryRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof ApiInterface
     */
    getC2CTradeHistory(
        requestParameters?: GetC2CTradeHistoryRequest
    ): Promise<RestApiResponse<GetC2CTradeHistoryResponse>>;
}

/**
 * Request parameters for getC2CTradeHistory operation in Api.
 * @interface GetC2CTradeHistoryRequest
 */
export interface GetC2CTradeHistoryRequest {
    /**
     * Trade side filter
     * @type {'BUY' | 'SELL'}
     * @memberof ApiGetC2CTradeHistory
     */
    readonly tradeType?: GetC2CTradeHistoryTradeTypeEnum;

    /**
     *
     * @type {number | bigint}
     * @memberof ApiGetC2CTradeHistory
     */
    readonly startTimestamp?: number | bigint;

    /**
     *
     * @type {number | bigint}
     * @memberof ApiGetC2CTradeHistory
     */
    readonly endTimestamp?: number | bigint;

    /**
     * Page number
     * @type {number | bigint}
     * @memberof ApiGetC2CTradeHistory
     */
    readonly page?: number | bigint;

    /**
     * Number of records per page
     * @type {number | bigint}
     * @memberof ApiGetC2CTradeHistory
     */
    readonly rows?: number | bigint;
}

/**
 * Api - object-oriented interface
 * @class Api
 */
export class Api implements ApiInterface {
    private readonly configuration: ConfigurationRestAPI;
    private localVarAxiosParamCreator;

    constructor(configuration: ConfigurationRestAPI) {
        this.configuration = configuration;
        this.localVarAxiosParamCreator = ApiAxiosParamCreator(configuration);
    }

    /**
     * Get C2C Trade History
     *
     * Weight(IP): 1
     *
     * Security Type: USER_DATA
     *
     * Notes:
     * - The max interval between `startTimestamp` and `endTimestamp` is 30 days.
     * - If `startTimestamp` and `endTimestamp` are not sent, the recent 30 days' data is returned.
     * - You can only view data from the past 6 months. For all C2C orders, check `https://c2c.binance.com/en/fiatOrder`.
     *
     * @summary Get C2C Trade History (USER_DATA)
     * @param {GetC2CTradeHistoryRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<GetC2CTradeHistoryResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof Api
     * @see {@link https://developers.binance.com/en/docs/catalog/investment-and-services-c2-c/api/rest-api/~#get-c2-ctrade-history Binance API Documentation}
     */
    public async getC2CTradeHistory(
        requestParameters: GetC2CTradeHistoryRequest = {}
    ): Promise<RestApiResponse<GetC2CTradeHistoryResponse>> {
        const localVarAxiosArgs = await this.localVarAxiosParamCreator.getC2CTradeHistory(
            requestParameters?.tradeType,
            requestParameters?.startTimestamp,
            requestParameters?.endTimestamp,
            requestParameters?.page,
            requestParameters?.rows
        );
        return sendRequest<GetC2CTradeHistoryResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: false }
        );
    }
}

export enum GetC2CTradeHistoryTradeTypeEnum {
    BUY = 'BUY',
    SELL = 'SELL',
}
